import api from './api';

// ============================================
// TYPES
// ============================================

export interface BillingOverview {
    subscription: {
        id: string;
        status: 'active' | 'trial' | 'expired' | 'cancelled' | 'suspended' | 'pending_payment';
        billingCycle: 'monthly' | 'yearly';
        startDate: string;
        endDate: string;
        autoRenew: boolean;
        daysRemaining: number;
        isExpired: boolean;
    };
    plan: {
        id: string;
        name: string;
        description?: string;
        monthlyPrice: number;
        yearlyPrice: number;
        features: string[];
    } | null;
    lastPayment: {
        amount: number;
        date: string;
        invoiceNumber: string;
        billingCycle: 'monthly' | 'yearly';
        paymentMethod: string;
    } | null;
    stats: {
        totalPayments: number;
        totalPaid: number;
    };
}

export interface Payment {
    _id: string;
    schoolId: string;
    subscriptionId: string;
    planId: { _id: string; name: string } | string;
    amount: number;
    currency: string;
    billingCycle: 'monthly' | 'yearly';
    paymentMethod: 'bank_transfer' | 'cash' | 'cheque' | 'online';
    paymentDate: string;
    referenceNumber?: string;
    notes?: string;
    invoiceNumber: string;
    markedBy: { _id: string; name: string } | string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface SubscriptionHistoryItem {
    _id: string;
    subscriptionId: string;
    schoolId: string;
    action: 'created' | 'plan_changed' | 'billing_cycle_changed' | 'extended' | 'suspended' | 'resumed' | 'cancelled' | 'expired' | 'renewed';
    previousPlanId?: { _id: string; name: string };
    newPlanId?: { _id: string; name: string };
    previousBillingCycle?: 'monthly' | 'yearly';
    newBillingCycle?: 'monthly' | 'yearly';
    previousEndDate?: string;
    newEndDate?: string;
    previousStatus?: string;
    newStatus?: string;
    notes?: string;
    performedBy: { _id: string; name: string; email: string };
    createdAt: string;
}

export interface Pagination {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

// ============================================
// BILLING API
// ============================================

/**
 * Get billing overview for the current user's school
 */
export const getBillingOverview = async (): Promise<BillingOverview> => {
    const response = await api.get('/billing/overview');
    return response.data.data;
};

/**
 * Get payment history for the current user's school
 */
export const getMyPayments = async (params?: {
    page?: number;
    limit?: number;
}): Promise<{ data: Payment[]; pagination: Pagination }> => {
    const response = await api.get('/billing/payments', { params });
    return { data: response.data.data, pagination: response.data.pagination };
};

/**
 * Get subscription history for the current user's school
 */
export const getMySubscriptionHistory = async (params?: {
    page?: number;
    limit?: number;
}): Promise<{ data: SubscriptionHistoryItem[]; pagination: Pagination }> => {
    const response = await api.get('/billing/history', { params });
    return { data: response.data.data, pagination: response.data.pagination };
};

/** A subscription payment's invoice, laid out for the PDF drawn in the browser. */
export interface PlatformInvoicePrint {
    invoiceNumber: string;
    date: string;
    method: string;
    billTo: { name: string; address: string | null; email: string | null; phone: string | null };
    planName: string;
    billingCycle: string;
    amount: number;
    currency: string;
    reference: string | null;
    notes: string | null;
    receivedBy: string;
}

/** One of the school's own payments' invoice. */
export const getInvoice = async (paymentId: string): Promise<PlatformInvoicePrint> => {
    const response = await api.get(`/billing/payments/${paymentId}/invoice`);
    if (!response.data.success) throw new Error(response.data.message);
    return response.data.data;
};

const billingService = {
    getBillingOverview,
    getMyPayments,
    getMySubscriptionHistory,
    getInvoice,
};

export default billingService;
